import {
  toWSDateString,
  WSTableCell,
  WSTableColumn
} from "@wingspanhq/fe-component-library";
import { formatMoney } from "@wingspanhq/utils/dist/formatMoney";
import { PayrollRun, PayrollRunCycleType } from "@wingspanhq/wingspan-api-sdk";

import { PAYROLL_CURRENCY } from "../../constants";
import { selectorPayrollRunDatePaid } from "../../selectors/selectorPayrollRunDatePaid";
import { selectorPayrollRunStatusPill } from "../../selectors/selectorPayrollRunStatusPill";
import { parsePayrollAmount } from "../../utils";
import { BreakdownCountCell } from "../BreakdownCountCell";

const EMPTY_VALUE = "—";

/**
 * The contractor payroll runs table, column for column the retired v1
 * `PayablesPayrollRuns` screen: date paid, payout count, status, amount.
 *
 * Deliberately not the employee table. The Payables tab is the surface payers
 * already knew, so it keeps its own shape; the dedicated payroll-runs module
 * follows the new payroll design instead.
 *
 * Two things v1 did that v3 cannot:
 *
 * - **Sorting.** "Date paid" was sortable and defaulted to newest first.
 *   `listPayrollRuns` accepts no `sort` parameter, so the header is inert and
 *   the order is whatever the endpoint returns (gap G3).
 * - **The status vocabulary.** v1 read invoice statuses and showed only
 *   "Processed" / "Action required". A run is its own resource on v3 with eight
 *   states, so the pill uses those — mapping them onto two invoice states would
 *   leave a draft, cancelled or failed run with a blank status cell.
 */
export const getColumnsContractor = (): WSTableColumn<PayrollRun>[] => [
  {
    config: { header: { text: "Date paid" }, gridTemplateSizeMax: "1fr" },
    renderFunction: rowItem => {
      const datePaid = selectorPayrollRunDatePaid(rowItem.data);

      return (
        <WSTableCell
          secondaryText={
            rowItem.data.cycleType === PayrollRunCycleType.OFF_CYCLE
              ? "Off-Cycle"
              : undefined
          }
          text={
            datePaid ? toWSDateString(datePaid, "monthDayYear") : EMPTY_VALUE
          }
        />
      );
    }
  },
  {
    config: {
      header: { text: "Total payouts" },
      gridTemplateSizeMax: "1fr",
      hideOnScreens: ["XS"]
    },
    renderFunction: rowItem => (
      <BreakdownCountCell payrollRunId={rowItem.data.id ?? ""} />
    )
  },
  {
    config: { header: { text: "Status" }, gridTemplateSizeMax: "1fr" },
    // Pill only, as v1 had it — the secondary status line belongs to the new
    // employee design.
    renderFunction: rowItem => (
      <WSTableCell pill={selectorPayrollRunStatusPill(rowItem.data.status)} />
    )
  },
  {
    config: { header: { text: "Amount" }, justify: "end" },
    renderFunction: rowItem => (
      <WSTableCell
        text={formatMoney(
          parsePayrollAmount(rowItem.data.totalAmount),
          PAYROLL_CURRENCY
        )}
      />
    )
  }
];
